"use client";

// Dependencies
import { useTransition } from "react";
import * as zod from "zod";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";

// Components
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/toaster";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

// Libs
import { PasswordResetRequestSchema } from "@/lib/zod";

// Hooks
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";

// Actions
import { passwordReset } from "@/actions/auth/passwordReset";

const PasswordResetForm = () => {
  const router = useRouter();
  const { toast } = useToast();

  const [isPending, startTransition] = useTransition();

  const form = useForm<zod.infer<typeof PasswordResetRequestSchema>>({
    resolver: zodResolver(PasswordResetRequestSchema),
    defaultValues: {
      correo: "",
    },
  });

  const handleSubmit = async (
    values: zod.infer<typeof PasswordResetRequestSchema>
  ) => {
    const { correo: email } = values;

    startTransition(async () => {
      const res = await passwordReset(email);

      if (res.success) {
        router.push("/accounts/password-reset/confirmation");
      } else {
        toast({
          title: "ERROR",
          description:
            "Hubo un error intentando acceder a tu cuenta. Verifica tus datos.",
          variant: "destructive",
        });
      }
    });
  };

  return (
    <article>
      <Toaster />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="w-full mb-4"
          autoComplete="off"
        >
          <FormField
            control={form.control}
            name="correo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Correo</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Correo"
                    {...field}
                    autoComplete="off"
                  />
                </FormControl>
                <FormDescription></FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormControl className="mt-4">
            <Button type="submit" className="w-full" disabled={isPending}>
              Cambiar contraseña
            </Button>
          </FormControl>
        </form>
      </Form>
      <Separator className="mb-4" />
    </article>
  );
};

export default PasswordResetForm;
